/**
 * The session header: a strip along the top of one conversation (RFC 0278).
 *
 * THE ASK, from the other side. RFC 0278 wanted apps in the agent panel's
 * header, with a fill whose source is a projection of a document the
 * surface already holds. This is that seam: the pane hands over the session and
 * the transcript it is already rendering, and whatever fills the slot decides
 * what to say about them.
 *
 * WHY IT IS NOT THE `header` SLOT. That one is the window's top strip, filled
 * once, and a widget about "this session" put there would be about whichever
 * pane happened to have focus — or about nothing, in a window with two
 * conversations side by side. This slot is per pane and carries the session
 * with it, which is the only way two panes on two sessions can each say
 * something true.
 *
 * A LIST, not a fill-once surface. Several apps can each add a widget — what
 * the session changed, what it is holding, which branch it is on — and they sit
 * one after another. So a silent layout draws EVERYTHING that declares the slot
 * rather than the built-in alone (`fillSlotAll`), because installing an app
 * that adds a widget and then having to arrange a layout to see it is not
 * installing anything.
 *
 * WHAT IS SHIPPED IN IT. One widget, `mod.sessionChanges`: the files and
 * documents this session created or edited. It is a BUILT-IN rather than a
 * feature of the Review app, because what a session changed is on the log
 * whether or not anything is installed, and gating the strip on Review meant a
 * fresh project saw nothing and had no way to find out why. Review is where
 * one of those changes is read as a diff, which is a link out, not a licence.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import type { ReactNode } from 'react'
import { SessionChangesView } from '../../../features/thread/SessionChangesView'
import { SessionTasksChip } from '../../../features/thread/SessionTasksChip'
import type { SessionChanges } from '../../../features/thread/sessionChanges'
import type { BuiltinFill } from '../slots'

/**
 * What the session header hands its fills.
 *
 * The transcript is passed as the FOLD rather than as itself: every widget
 * about "what happened in this session" would otherwise re-walk the same
 * megabyte, once per widget, on every streamed token. `partial` travels with it
 * because the fold is over a WINDOW of the transcript — a widget that reported
 * a count without knowing that would be confidently short.
 */
export interface SessionHeaderSlotProps {
  sessionId: string
  /** The session's project, to name its files by their place in it. */
  projectId?: string
  changes: SessionChanges
  /** Earlier transcript pages exist and were not read. */
  partial: boolean
  /** Titles for anything the fold could only name by id. */
  documents?: { id: string; title: string }[]
  /** Put a document on the canvas. Absent in a pane that cannot open one. */
  onOpenDocument?: (documentId: string) => void
  /** Show this session's full Changes in place of its thread (RFC 0313 3h). */
  onViewMore?: () => void
  fileSourceId?: string
  reviewBase?: string
  reviewBranch?: string
  onReviewBranch?: (branch: string) => void
  /**
   * What the tasks chip needs (RFC 0319 3d). Tasks are core project state, so
   * this depends on the project being available, not on an app installation.
   */
  tasks?: { repo: Repo; projectId: string; projectDoc: unknown }
}

export const BUILTIN_SESSION_CHANGES_ID = 'mod.sessionChanges'

export const builtinSessionChanges: BuiltinFill<'sessionHeader'> = {
  id: BUILTIN_SESSION_CHANGES_ID,
  title: 'What this session changed',
  slot: 'sessionHeader',
  builtin: true,
  render: (props: SessionHeaderSlotProps): ReactNode => (
    <SessionChangesView
      changes={props.changes}
      partial={props.partial}
      {...(props.projectId ? { projectId: props.projectId } : {})}
      {...(props.documents ? { documents: props.documents } : {})}
      {...(props.onOpenDocument
        ? { onOpenDocument: props.onOpenDocument }
        : {})}
      {...(props.onViewMore ? { onViewMore: props.onViewMore } : {})}
      {...(props.fileSourceId ? { fileSourceId: props.fileSourceId } : {})}
      {...(props.reviewBase ? { reviewBase: props.reviewBase } : {})}
      {...(props.reviewBranch ? { reviewBranch: props.reviewBranch } : {})}
      {...(props.onReviewBranch
        ? { onReviewBranch: props.onReviewBranch }
        : {})}
    />
  ),
}

export const BUILTIN_SESSION_TASKS_ID = 'mod.sessionTasks'

/** The tasks this session is working, as a chip (RFC 0319 3d). */
export const builtinSessionTasks: BuiltinFill<'sessionHeader'> = {
  id: BUILTIN_SESSION_TASKS_ID,
  title: 'Tasks this session is working',
  slot: 'sessionHeader',
  builtin: true,
  render: (props: SessionHeaderSlotProps): ReactNode =>
    props.tasks ? (
      <SessionTasksChip
        repo={props.tasks.repo}
        projectId={props.tasks.projectId}
        projectDoc={props.tasks.projectDoc}
        sessionId={props.sessionId}
        {...(props.onOpenDocument
          ? { onOpenDocument: props.onOpenDocument }
          : {})}
      />
    ) : null,
}
